import { isRight } from 'fp-ts/Either';
import { useCallback, useEffect, useState } from 'react';
import type { RightResult, RouterOutputs } from '../types/TRPC';
import { captureAndReportError } from '../utils/errorReporting';
import { hasDappStaffAccess, isAuthenticated } from '../utils/roleUtils';
import { useAuth } from './useAuth';
import { useCategoryAssignmentAccess } from './useCategoryAssignmentAccess';
import { useTRPC } from './useTRPC';

type CategoryInfo = RightResult<RouterOutputs['getDappCategoryInfo']>;

export interface DappStoreStatus {
  /** True for moderators/admins (their publish right comes from staff capabilities). */
  isStaff: boolean;
  isLoading: boolean;
  /** True once the first Store lookup settled (Right or Left) for this dapp. */
  hasResolved: boolean;
  storeIngested: boolean | null;
  storeStatus: 'published' | 'unpublished' | null;
  categories: string[];
  /** Whether the viewer may trigger the Store publish ("Go Live Now"). */
  canPublish: boolean;
  refetch: () => void;
}

/**
 * Store-side publication state + categories for a dapp. Readable by staff and
 * dapp owners; a Left (e.g. not ingested yet) yields `null`s without toasting.
 */
export function useDappStoreStatus(
  dappId: string | null | undefined,
): DappStoreStatus {
  const trpc = useTRPC();
  const [authState] = useAuth();
  const access = useCategoryAssignmentAccess();
  const isStaff =
    isAuthenticated(authState) &&
    hasDappStaffAccess(authState.user.role, authState.user.email);
  const [info, setInfo] = useState<CategoryInfo | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasResolved, setHasResolved] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!dappId) {
      setInfo(null);
      setIsLoading(false);
      setHasResolved(true);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    void trpc.getDappCategoryInfo
      .query({ dappId })
      .then((result) => {
        if (cancelled) {
          return;
        }
        setInfo(isRight(result) ? result.right : null);
      })
      .catch((loadError) => {
        if (cancelled) {
          return;
        }
        captureAndReportError('Failed to load dapp store status', loadError);
        setInfo(null);
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
          setHasResolved(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [dappId, refreshKey, trpc]);

  const refetch = useCallback(() => setRefreshKey((key) => key + 1), []);

  return {
    isStaff,
    isLoading: isLoading || (isStaff && access.isLoading),
    hasResolved,
    storeIngested: info?.storeIngested ?? null,
    storeStatus: info?.storeStatus ?? null,
    categories: info?.categories ?? [],
    // Go Live is staff-only, as it was before the redesign: `publishDapp` is
    // gated on the `canPublish` staff capability, so offering the button to a
    // publisher with SubmitToStore would only produce a 403. Restore the
    // `options.canSubmitToStore` branch alongside the API gate in
    // api/src/lib/dappPublishService.ts if publishers should self-publish.
    canPublish: isStaff && access.canPublish,
    refetch,
  };
}
